import {
  initialVehicleType,
  IVehicleTypeModel,
  tabOptions,
  validationvehicleType,
} from './VehicleTypeModel'
import {createVehicleType, deleteVehicleType, loadVehicleTypes, updateVehicleType} from './Api'
import React, {FC, useCallback, useEffect, useState} from 'react'
import {Form, Formik, FormikProps} from 'formik'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import FormTabHeader from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import {VehicleTypeTable} from './VehicleTypeTable'
import {branches, brands, vehicleGroups} from '../../../../shared/models/SharedApiLookups'
import {FUEL_TYPES, TRANSMISSION_TYPES} from '../../../../shared/models/SharedConstantLookups'

export const VehicleTypePage: FC = () => {
  const [tab, setTab] = useState<string | 'form'>('form')
  const [vehicletypes, setVehicleTypes] = useState<IVehicleTypeModel[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null | undefined>(null)
  const [showModal, setShowModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [vehicleGroupOptions, setVehicleGroupOptions] = useState<{value: string; label: string}[]>(
    []
  )
  const [brandOptions, setBrandOptions] = useState<{value: string; label: string}[]>([])
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])

  useEffect(() => {
    Promise.all([vehicleGroups(), brands(), branches()]).then(([vg, br, brn]) => {
      setVehicleGroupOptions(vg)
      setBrandOptions(br)
      setBranchOptions(brn)
    })
  }, [])

  const fetchVehicleTypes = useCallback(
    async (page: number = 0, _sort?: string, _order?: 'asc' | 'desc') => {
      try {
        setLoading(true)
        const res = await loadVehicleTypes(page)
        const data = res.data.payload || res.data
        setVehicleTypes(data.content || data || [])
        setTotalPages(data.totalPages || 1)
      } catch (err: any) {
        if (err?.response?.status === 401) {
          window.location.href = '/login'
        }
        console.error(err)
        showErrorToast('Kayıtlar getirilirken hata oluştu.')
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    fetchVehicleTypes(currentPage, sortBy, sortOrder)
  }, [fetchVehicleTypes, currentPage, sortBy, sortOrder])

  const handleSave = async (values: IVehicleTypeModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateVehicleType({...values, id: selectedId})
        showSuccessToast('Araç Tipi başarıyla güncellendi')
      } else {
        await createVehicleType(values)
        showSuccessToast('Araç Tipi başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchVehicleTypes(0, sortBy, sortOrder)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (loc: IVehicleTypeModel, formik: FormikProps<IVehicleTypeModel>) => {
    formik.setValues(loc)
    setSelectedId(loc.id)
    setTab('form')
  }

  const handleDeleteClick = useCallback((row: IVehicleTypeModel) => {
    setSelectedId(row.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (!selectedId) return
    try {
      await deleteVehicleType(selectedId)
      showSuccessToast('Araç Tipi başarıyla silindi')
      await fetchVehicleTypes(currentPage, sortBy, sortOrder)
    } catch (err) {
      console.error(err)
      showErrorToast('Araç Tipi silinirken bir hata oluştu')
    } finally {
      setShowModal(false)
      setSelectedId(null)
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchVehicleTypes(newPage, sortBy, sortOrder) 
      }
    },
    [totalPages, sortBy, sortOrder, fetchVehicleTypes]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchVehicleTypes(currentPage, column, order)
    },
    [currentPage, fetchVehicleTypes]
  )

  return (
    <>
      <Formik
        initialValues={initialVehicleType}
        validationSchema={validationvehicleType}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }

          return (
            <>
              <FormTabHeader
                formId='vehicleTypeForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />

              {tab === 'form' && (
                <div className='card mb-5'>
                  <Form id='vehicleTypeForm' className='form card-body'>
                    <div className='row g-5 align-items-center mb-2'>
                      <SelectInput
                        name='brandId'
                        label='Marka'
                        wrapperClassName='col-md-6'
                        options={brandOptions}
                      />
                      <TextInput
                        name='name'
                        label='Araç Tipi (Model)'
                        placeholder='Araç Tipi Yazınız!'
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='transmission'
                        label='Vites'
                        wrapperClassName='col-md-6'
                        options={TRANSMISSION_TYPES}
                      />
                      <SelectInput
                        name='fuelType'
                        label='Yakıt Türü'
                        wrapperClassName='col-md-6'
                        options={FUEL_TYPES}
                      />
                      <SelectInput
                        name='vehicleGroupId'
                        label='Araç Grubu'
                        wrapperClassName='col-md-6'
                        options={vehicleGroupOptions}
                      />
                      <SelectInput
                        name='branch'
                        label='Şube'
                        wrapperClassName='col-md-6'
                        options={branchOptions}
                      />
                    </div>
                  </Form>
                </div>
              )}

              {tab === 'list' && (
                <div className='card'>
                  <div className='card-body pt-0'>
                    <VehicleTypeTable
                      data={vehicletypes}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onEdit={(loc) => handleEdit(loc, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export default VehicleTypePage
